/// <reference lib="dom" />
/**
 * saveManager.ts
 * localStorage wrapper for all Starfall Courier save operations.
 *
 * IMPORTANT: Never call SaveManager methods from the game loop (update cycle).
 * All methods are async (returning resolved Promises) — dispatch save events
 * between frames or on checkpoint touch.
 *
 * Schema, defaults, and validation logic live in `saveData.ts` so storage
 * concerns stay isolated.
 *
 * See ROADMAP.md Phase 2.
 *
 * @module saveManager
 */

import type { SaveData } from '../types/save';
import { SAVE_KEY, createDefaultSave, serialiseSaveData, validateSaveData } from './saveData';
import { getStorage } from './storageBackend';
import Logger from '../utils/logger';

export { SCHEMA_VERSION, createDefaultSave } from './saveData';

/**
 * Persist the given save data to `localStorage`.
 * The stored payload always uses the current `SCHEMA_VERSION` — the value in
 * `data.schemaVersion` is ignored and overridden so a stale caller can never
 * write a payload that `load()` will immediately discard as a schema mismatch.
 * Call this from checkpoint activation or world-complete events — never from the game loop.
 * @param data - The save payload to write.
 * @returns A promise that resolves when the write completes.
 */
export async function save(data: SaveData): Promise<void> {
  const storage = getStorage();
  if (!storage) {
    Logger.warn('SaveManager', 'No localStorage available; save dropped');
    return;
  }
  try {
    storage.setItem(SAVE_KEY, serialiseSaveData(data));
    Logger.debug('SaveManager', 'Save written successfully');
  } catch (err) {
    Logger.error('SaveManager', 'Failed to write save data', err);
    throw err;
  }
}

/**
 * Load the persisted save data from `localStorage`.
 * Returns null when no save exists or the schema version does not match.
 * @returns The saved `SaveData`, or null if unavailable/incompatible.
 */
export async function load(): Promise<SaveData | null> {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const json = storage.getItem(SAVE_KEY);
    if (json === null) return null;
    return validateSaveData(JSON.parse(json));
  } catch (err) {
    Logger.error('SaveManager', 'Failed to read save data', err);
    return null;
  }
}

/**
 * Erase all save data from `localStorage`.
 * Used for "New Game" and during testing.
 * @returns A promise that resolves when the key has been removed.
 */
export async function reset(): Promise<void> {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(SAVE_KEY);
    Logger.debug('SaveManager', 'Save data cleared');
  } catch (err) {
    Logger.error('SaveManager', 'Failed to clear save data', err);
    throw err;
  }
}

const SaveManager = { save, load, reset, createDefaultSave };
export default SaveManager;
